import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  Card,
  CardContent,
} from '@mui/material'
import {
  Close,
  Edit,
  Print,
  Download,
  CalendarToday,
  Person,
  ShoppingCart,
  AttachMoney,
  Flag,
} from '@mui/icons-material'

interface OrderDetailsDialogProps {
  open: boolean
  onClose: () => void
  order?: any
  onEdit?: (order: any) => void
}

export const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({
  open,
  onClose,
  order,
  onEdit,
}) => {
  if (!order) return null

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'in_progress':
        return 'warning'
      case 'pending':
        return 'info'
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'error'
      case 'high':
        return 'warning'
      case 'medium':
        return 'info'
      case 'low':
        return 'success'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
    }).format(amount)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundColor: 'surface',
          border: '1px solid',
          borderColor: 'outlineVariant',
          boxShadow: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: 'surfaceContainer',
          color: 'onSurface',
          fontWeight: 500,
          fontSize: '1.5rem',
          py: 3,
          px: 4,
          borderBottom: '1px solid',
          borderColor: 'outlineVariant',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.5rem', mb: 0.5 }}>
              Order Details
            </Typography>
            <Typography variant="body2" color="onSurfaceVariant">
              {order.order_number}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'onSurfaceVariant',
              '&:hover': {
                backgroundColor: 'surfaceVariant',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: 'surface',
          py: 4,
          px: 4,
        }}
      >
        <Grid container spacing={3}>
          {/* Status and Priority */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                label={order.status}
                color={getStatusColor(order.status) as any}
                variant="filled"
                size="medium"
              />
              <Chip
                label={`${order.priority} Priority`}
                color={getPriorityColor(order.priority) as any}
                variant="outlined"
                size="medium"
                icon={<Flag />}
              />
            </Box>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: 'surfaceContainer',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'outlineVariant',
                boxShadow: 1,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'onSurface', fontWeight: 500 }}>
                  <Person sx={{ color: 'primary.main' }} />
                  Customer Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Customer Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {order.customer_name}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Product Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: 'surfaceContainer',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'outlineVariant',
                boxShadow: 1,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'onSurface', fontWeight: 500 }}>
                  <ShoppingCart sx={{ color: 'primary.main' }} />
                  Product Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Product Type
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {order.product_type}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Quantity
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {order.quantity.toLocaleString()} units
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: 'surfaceContainer',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'outlineVariant',
                boxShadow: 1,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'onSurface', fontWeight: 500 }}>
                  <AttachMoney sx={{ color: 'primary.main' }} />
                  Financial Details
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Unit Price
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {formatCurrency(order.unit_price)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Total Amount
                  </Typography>
                  <Typography variant="h6" color="primary.main" fontWeight="bold">
                    {formatCurrency(order.total_amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Timeline Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: 'surfaceContainer',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'outlineVariant',
                boxShadow: 1,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'onSurface', fontWeight: 500 }}>
                  <CalendarToday sx={{ color: 'primary.main' }} />
                  Timeline
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Order Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {formatDate(order.order_date)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Due Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {formatDate(order.due_date)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="onSurfaceVariant" sx={{ mb: 0.5 }}>
                    Days Remaining
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="onSurface">
                    {Math.ceil((new Date(order.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Description */}
          {order.description && (
            <Grid item xs={12}>
              <Card
                sx={{
                  backgroundColor: 'surfaceContainer',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'outlineVariant',
                  boxShadow: 1,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'onSurface', fontWeight: 500 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" color="onSurfaceVariant">
                    {order.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Audit Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: 'outlineVariant' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'onSurfaceVariant' }}>
              <Typography variant="caption">
                Created: {formatDate(order.created_at)}
              </Typography>
              <Typography variant="caption">
                Last Updated: {formatDate(order.updated_at)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: 'surfaceContainer',
          borderTop: '1px solid',
          borderColor: 'outlineVariant',
          p: 4,
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{
            borderRadius: 7,
            borderColor: 'outline',
            color: 'onSurface',
            fontWeight: 500,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: 'surfaceVariant',
              borderColor: 'onSurface',
            },
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<Download />}
          sx={{
            borderRadius: 7,
            borderColor: 'outline',
            color: 'onSurface',
            fontWeight: 500,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: 'surfaceVariant',
              borderColor: 'onSurface',
            },
          }}
        >
          Export
        </Button>
        {onEdit && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => onEdit(order)}
            sx={{
              borderRadius: 7,
              backgroundColor: 'primary.main',
              color: 'onPrimary',
              fontWeight: 500,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: 4,
              },
              '&:active': {
                boxShadow: 1,
              },
            }}
          >
            Edit Order
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
